<template>
  <div class="agent-title">
    <div
      v-if="agentKey === 'assistant'"
      class="agent-avatar agent-avatar--icon"
    >
      <AssistantIcon />
    </div>
    <div v-else-if="agentAvatar" class="agent-avatar">
      <img :src="agentAvatar" :alt="displayName" />
    </div>
    <div v-else class="avatar-placeholder">
      {{ (displayName === "Loading..." ? "A" : displayName).charAt(0) }}
    </div>
    <span class="name">{{ displayName }}</span>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";

import type { AiAgent } from "@ctrl/controller-rpc";

import { getAvatarUrlByKey } from "@/components/ai/AgentAvatar.vue";
import {
  getAgentAvatar,
  getAgentName,
  getCustomAgentOverride,
} from "@/components/ai/useAgents";
import AssistantIcon from "@/components/icons/AssistantIcon.vue";
import * as inject from "@/components/inject";
import { bindAsync, useReleaser } from "@/ref";

export default defineComponent({
  name: "AgentTitle",
  components: { AssistantIcon },
  props: {
    agentId: { type: String, required: true },
    organizationId: { type: String, required: true },
  },
  async setup(props) {
    const controller = inject.controller();

    const releaser = useReleaser();
    const agentId = computed(() => props.agentId);
    const agentRef = await releaser.add(
      bindAsync(agentId, (id: string) =>
        controller.ai.agent.get(props.organizationId, id)
      )
    );

    const agentData = computed((): AiAgent | null => agentRef.value ?? null);
    const agentName = computed(() => agentData.value?.name ?? null);
    const agentKey = computed(() => agentData.value?.key ?? null);
    const agentAvatar = computed(() => {
      const key = agentData.value?.key;
      if (typeof key !== "string") return "";
      const catalogUrl = getAgentAvatar(key);
      if (catalogUrl) return catalogUrl;
      const override = getCustomAgentOverride(props.agentId);
      return override?.avatar ? getAvatarUrlByKey(override.avatar) : "";
    });
    const displayName = computed(() => {
      const data = agentData.value;
      if (!data) return "Loading...";
      const catalogName = data.key ? getAgentName(data.key) : "";
      const override = getCustomAgentOverride(props.agentId);
      return (
        data.name ?? (catalogName || undefined) ?? override?.name ?? "Agent"
      );
    });

    return {
      agentAvatar,
      agentKey,
      agentName,
      displayName,
    };
  },
});
</script>

<style scoped lang="scss">
.agent-title {
  display: flex;
  align-items: center;
  width: 100%;

  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1;
    margin-top: -1px;
  }
}

.agent-avatar {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &--icon {
    justify-content: center;
  }
}

.avatar-placeholder {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  @include themed() {
    background: t($grey-15);
  }
}
</style>
